/**
 * LookupCategoryDropdown — Stage-1 picker for the `/` slash-command.
 *
 * Shown immediately after the user types `/` (and before they pick a category).
 * Mirrors `LookupSuggestionDropdown` styling so the two stages feel like one
 * continuous floating UI: same container shadow, same row height/colours, same
 * keyboard-driven highlight. The parent stays responsible for keyboard events
 * (arrows + Enter) so the same `highlightIndex` model used in stage 2 applies.
 */

import React from 'react';
import type { LookupCategory } from '../AskAsperAgentic.types';

export interface LookupCategoryDropdownProps {
    categories: LookupCategory[];
    highlightIndex: number;
    onHighlight: (index: number) => void;
    onSelect: (index: number) => void;
    className?: string;
    'data-test-id'?: string;
}

const LookupCategoryDropdown: React.FC<LookupCategoryDropdownProps> = ({
    categories,
    highlightIndex,
    onHighlight,
    onSelect,
    className,
    'data-test-id': dataTestId,
}) => {
    if (!categories || categories.length === 0) return null;

    return (
        <div
            className={`absolute bottom-full left-0 right-0 mb-2 z-50 bg-white rounded-[12px] border border-[#e8eafa] shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)] overflow-hidden ${
                className ?? ''
            }`}
            role="listbox"
            data-test-id={dataTestId}
            data-lookup-category-dropdown
            // Prevent the textarea from losing focus when clicking inside the dropdown.
            onMouseDown={(e) => e.preventDefault()}
        >
            <div className="py-1">
                {categories.map((category, idx) => {
                    const isActive = idx === highlightIndex;
                    return (
                        <div
                            key={category.id}
                            role="option"
                            aria-selected={isActive}
                            className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${
                                isActive ? 'bg-[#f5f3ff]' : 'bg-transparent hover:bg-[#fafafa]'
                            }`}
                            onMouseEnter={() => onHighlight(idx)}
                            onClick={() => onSelect(idx)}
                            data-test-id={
                                dataTestId ? `${dataTestId}-option-${category.id}` : undefined
                            }
                        >
                            <span className="font-['Open_Sans'] font-medium text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate">
                                {category.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LookupCategoryDropdown;
